/**
 * Bot battle screen.
 * Renders the same battle layout as PvP but without P2P sync, chat, or rematch logic.
 * Player selects actions, bot responds instantly.
 */

import type { ScreenId } from "./home";
import type { BattleState, BattleAction, PokemonBattleState } from "../../engine/types";
import { el, clearChildren } from "../utils/dom";
import { button, toast } from "../components";
import { pokemonBackUrl, pokemonFrontUrl, pokemonMenuUrl, backgroundImageUrl } from "../../data/images";
import { resolveSpriteUrl } from "../../data/sprites";
import { getBotBattleSession, resolveBotTurn, clearBotBattleSession, getBotReplay } from "../bot-session";
import { dbPut } from "../../data/db";
import { DB_STORES, REDUCED_MOTION_QUERY } from "../../config/constants";

export function renderBotBattle(
  container: HTMLElement,
  navigate: (screen: ScreenId) => void,
): void {
  clearChildren(container);

  const session = getBotBattleSession();
  if (!session) {
    toast("No active bot battle session", "error");
    navigate("bot-lobby");
    return;
  }

  const { engine, playerProfile, botProfile, config } = session;
  const mySide = "p1";
  const prefersReducedMotion = window.matchMedia(REDUCED_MOTION_QUERY).matches;

  const root = el("div", { className: "battle-root" });

  // Background
  const bg = el("div", { className: "battle-bg" });
  void resolveSpriteUrl(backgroundImageUrl(config.battleConfig.battleBackground)).then((url) => {
    if (url) bg.style.backgroundImage = `url(${url})`;
  });
  root.appendChild(bg);

  // Opponent area (top)
  const foeArea = el("div", { className: "battle-area battle-foe-area" });
  const foeInfo = el("div", { className: "battle-trainer-info" });
  foeInfo.appendChild(el("span", { text: botProfile.name, className: "trainer-name" }));
  foeArea.appendChild(foeInfo);
  const foeSprite = el("img", { className: "battle-sprite battle-foe-sprite sprite-animated", attrs: { alt: "Opponent Pokemon" } }) as HTMLImageElement;
  foeSprite.style.imageRendering = "pixelated";
  foeArea.appendChild(foeSprite);
  const foeHpBar = createHpBar();
  foeArea.appendChild(foeHpBar.container);
  root.appendChild(foeArea);

  // Player area (bottom)
  const myArea = el("div", { className: "battle-area battle-my-area" });
  const myInfo = el("div", { className: "battle-trainer-info" });
  myInfo.appendChild(el("span", { text: playerProfile.name, className: "trainer-name" }));
  myArea.appendChild(myInfo);
  const mySprite = el("img", { className: "battle-sprite battle-my-sprite sprite-animated", attrs: { alt: "My Pokemon" } }) as HTMLImageElement;
  mySprite.style.imageRendering = "pixelated";
  myArea.appendChild(mySprite);
  const myHpBar = createHpBar();
  myArea.appendChild(myHpBar.container);
  root.appendChild(myArea);

  // Turn info
  const turnInfo = el("div", { className: "battle-turn-info" });
  root.appendChild(turnInfo);

  // Minimal battle log (read-only)
  const battleLog = el("div", { className: "battle-log" });
  root.appendChild(battleLog);

  // Action panel
  const actionPanel = el("div", { className: "battle-action-panel" });
  root.appendChild(actionPanel);

  // Move target selector (hidden initially)
  const targetSelector = el("div", { className: "move-target-selector" });
  targetSelector.style.display = "none";
  root.appendChild(targetSelector);

  // End-of-battle UI
  const endArea = el("div", { className: "battle-end-area" });
  endArea.style.display = "none";
  root.appendChild(endArea);

  container.appendChild(root);

  // Status/condition icons container
  const foeStatus = el("div", { className: "battle-status-icons" });
  foeArea.appendChild(foeStatus);
  const myStatus = el("div", { className: "battle-status-icons" });
  myArea.appendChild(myStatus);

  function setSpritesPaused(paused: boolean): void {
    foeSprite.classList.toggle("sprite-paused", paused);
    mySprite.classList.toggle("sprite-paused", paused);
  }

  function updateSprite(img: HTMLImageElement, species: string, shiny: boolean | undefined, isBack: boolean): void {
    const url = isBack ? pokemonBackUrl(species, shiny) : pokemonFrontUrl(species, shiny);
    if (url) {
      void resolveSpriteUrl(url).then((resolved) => {
        if (resolved) {
          img.src = resolved;
          img.classList.toggle("sprite-animated", !prefersReducedMotion);
        }
      });
    }
  }

  function addLogLine(text: string): void {
    const line = el("div", { className: "battle-log-line", text });
    battleLog.appendChild(line);
    battleLog.scrollTop = battleLog.scrollHeight;
  }

  // Update UI to current state
  function updateUI(): void {
    const state = engine.getState();

    // Turn info
    turnInfo.innerHTML = "";
    if (state.ended && state.winner) {
      const isPlayerWin = state.winner === mySide;
      turnInfo.appendChild(el("h2", { text: isPlayerWin ? "You Win!" : "You Lose!", className: `battle-result ${isPlayerWin ? "victory" : "defeat"}` }));
    } else {
      turnInfo.appendChild(el("span", { text: `Turn ${state.turn}` }));
    }

    // Opponent sprite (front sprite of active Pokemon)
    const foePokemon = state.p2[state.activeP2];
    if (foePokemon) {
      updateSprite(foeSprite, foePokemon.species, foePokemon.shiny, false);
      updateHpBar(foeHpBar, foePokemon);
      foeStatus.innerHTML = foePokemon.status ? `<span class="status-icon status-${foePokemon.status.toLowerCase()}">${foePokemon.status}</span>` : "";
    }

    // Player sprite (back sprite of active Pokemon)
    const myPokemon = state.p1[state.activeP1];
    if (myPokemon) {
      updateSprite(mySprite, myPokemon.species, myPokemon.shiny, true);
      updateHpBar(myHpBar, myPokemon);
      myStatus.innerHTML = myPokemon.status ? `<span class="status-icon status-${myPokemon.status.toLowerCase()}">${myPokemon.status}</span>` : "";
    }

    // Action panel
    actionPanel.innerHTML = "";
    if (!session!.isEnded && !engine.isEnded()) {
      const legal = engine.getLegalActions(mySide);
      const moveActions = legal.filter((a): a is BattleAction & { type: "move" } => a.type === "move");
      const switchActions = legal.filter((a): a is BattleAction & { type: "switch" } => a.type === "switch");
      const struggleActions = legal.filter((a) => a.type === "struggle");

      // Move buttons
      if (moveActions.length > 0) {
        const moveSection = el("div", { className: "battle-moves" });
        const movesLabel = el("div", { className: "battle-section-label", text: "Moves" });
        moveSection.appendChild(movesLabel);
        const moveGrid = el("div", { className: "battle-move-grid" });
        for (const action of moveActions) {
          const moveBtn = el("button", {
            className: "btn btn-move",
            text: action.move,
            onClick: () => handleMoveClick(action),
          });
          moveGrid.appendChild(moveBtn);
        }
        moveSection.appendChild(moveGrid);
        actionPanel.appendChild(moveSection);
      }

      // Switch buttons
      if (switchActions.length > 0) {
        const switchSection = el("div", { className: "battle-switches" });
        const switchesLabel = el("div", { className: "battle-section-label", text: "Switch" });
        switchSection.appendChild(switchesLabel);
        for (const action of switchActions) {
          switchSection.appendChild(createSwitchButton(state, mySide, action, handleAction));
        }
        actionPanel.appendChild(switchSection);
      }

      // Struggle
      if (struggleActions.length > 0) {
        actionPanel.appendChild(button("Struggle", () => handleAction(struggleActions[0]), { variant: "danger" }));
      }

      // Forfeit
      actionPanel.appendChild(button("Forfeit", handleForfeit, { variant: "danger", className: "battle-forfeit-btn" }));
    }
  }

  function handleMoveClick(action: BattleAction & { type: "move" }): void {
    const state = engine.getState();
    const foeActiveCount = state.p2.filter((p) => p.active).length;
    const myActiveCount = state.p1.filter((p) => p.active).length;

    if (foeActiveCount <= 1 && myActiveCount <= 1) {
      handleAction(action);
      return;
    }

    // Multi-target situation — simplified: target first foe
    handleAction(action);
  }

  function describeAction(side: "p1" | "p2", action: BattleAction, state: BattleState): string {
    const trainerName = side === mySide ? playerProfile.name : botProfile.name;
    const activeMon = side === "p1" ? state.p1[state.activeP1] : state.p2[state.activeP2];
    const monName = activeMon?.nickname || activeMon?.species || "Pokemon";
    if (action.type === "move") {
      return `${trainerName}'s ${monName} used ${action.move}.`;
    } else if (action.type === "switch") {
      const team = side === "p1" ? state.p1 : state.p2;
      const target = team[action.slot];
      return `${trainerName} switched to ${target?.nickname || target?.species || "Pokemon"}.`;
    } else if (action.type === "struggle") {
      return `${trainerName}'s ${monName} used Struggle.`;
    }
    return `${trainerName} took an action.`;
  }

  function handleAction(action: BattleAction): void {
    if (session!.isEnded || engine.isEnded()) return;

    void (async () => {
      const preState = engine.getState();
      setSpritesPaused(true);
      await resolveBotTurn(session!, action);
      setSpritesPaused(false);
      const postState = engine.getState();

      // Log player and bot actions and any faints
      addLogLine(describeAction(mySide, action, preState));
      const botAction = getBotActionFromSession();
      if (botAction) {
        addLogLine(describeAction("p2", botAction, preState));
      }
      for (const mon of [...preState.p1, ...preState.p2]) {
        const postMon = [...postState.p1, ...postState.p2].find((p) => p.species === mon.species && p.active === mon.active);
        if (mon.hp > 0 && (postMon?.hp ?? 0) <= 0) {
          addLogLine(`${mon.nickname || mon.species} fainted!`);
        }
      }

      updateUI();
      await recordAndCheckEnd();
    })();
  }

  function getBotActionFromSession(): BattleAction | null {
    return session!.lastBotAction ?? null;
  }

  function handleForfeit(): void {
    session!.isEnded = true;
    turnInfo.innerHTML = "";
    turnInfo.appendChild(el("h2", { text: "You forfeited!", className: "battle-result defeat" }));
    endArea.style.display = "block";
    actionPanel.innerHTML = "";
    battleLog.style.display = "none";
    endArea.appendChild(button("Play Again", () => {
      clearBotBattleSession();
      navigate("bot-lobby");
    }));
    endArea.appendChild(button("Back to Home", () => {
      clearBotBattleSession();
      navigate("home");
    }));
  }

  async function recordAndCheckEnd(): Promise<void> {
    if (engine.isEnded()) {
      endArea.style.display = "block";
      actionPanel.innerHTML = "";
      endArea.innerHTML = "";
      battleLog.style.display = "none";

      // Save replay
      try {
        const replay = getBotReplay(session!);
        await dbPut(DB_STORES.REPLAYS, replay);
        toast("Replay saved!", "success");
      } catch {
        toast("Failed to save replay", "warning");
      }

      endArea.appendChild(button("Play Again", () => {
        clearBotBattleSession();
        navigate("bot-lobby");
      }, { variant: "primary" }));
      endArea.appendChild(button("Back to Home", () => {
        clearBotBattleSession();
        navigate("home");
      }, { variant: "secondary" }));
    }
  }

  // Initial render
  updateUI();
}

function createSwitchButton(
  state: BattleState,
  player: "p1" | "p2",
  action: BattleAction & { type: "switch" },
  onClick: (action: BattleAction) => void,
): HTMLElement {
  const team = player === "p1" ? state.p1 : state.p2;
  const pokemon = team[action.slot];
  const name = pokemon?.nickname || pokemon?.species || `Slot ${action.slot + 1}`;
  const isFainted = (pokemon?.hp ?? 0) <= 0;
  const isActive = pokemon?.active ?? false;

  const btn = el("button", {
    className: `btn btn-secondary switch-btn ${isFainted ? "switch-fainted" : ""} ${isActive ? "switch-active" : ""}`,
    onClick: () => onClick(action),
  });

  if (pokemon) {
    const menuUrl = pokemonMenuUrl(pokemon.species);
    if (menuUrl) {
      const img = el("img", { className: "switch-menu-sprite", attrs: { alt: name } }) as HTMLImageElement;
      img.style.imageRendering = "pixelated";
      void resolveSpriteUrl(menuUrl).then((resolved) => {
        if (resolved) img.src = resolved;
      });
      btn.appendChild(img);
    }
  }

  btn.appendChild(el("span", { text: `Switch to ${name}` }));
  return btn;
}

// --- HP bar helpers (adapted from battle.ts) ---

type HpBarElements = { container: HTMLElement; bar: HTMLElement; text: HTMLElement };

function createHpBar(): HpBarElements {
  const container = el("div", { className: "hp-bar-container" });
  const bar = el("div", { className: "hp-bar" });
  const text = el("span", { className: "hp-text" });
  container.appendChild(bar);
  container.appendChild(text);
  return { container, bar, text };
}

function updateHpBar(elements: HpBarElements, pokemon: PokemonBattleState): void {
  const pct = pokemon.maxHp > 0 ? (pokemon.hp / pokemon.maxHp) * 100 : 0;
  elements.bar.style.width = `${Math.max(0, pct)}%`;
  elements.bar.style.backgroundColor = pct > 50 ? "var(--success)" : pct > 20 ? "var(--warning)" : "var(--error)";
  elements.text.textContent = `${pokemon.hp}/${pokemon.maxHp}`;
}
